// CTSK-268 slice C — collapsible "Recent changes" panel for the
// registry tab surfaces (first tenant: the four Timeline Lane file
// tabs). Renders the last N entries of the underlying document's
// change history as {who, when, summary-of-changed-fields}.
//
// DATA SOURCE DECISION (per the brief's slice-C decision tree): the
// panel is AUDIT-LOG-DERIVED. A `document_versions` table exists in
// apps/api/src/db/schema.ts but NO endpoint serves it (the table is
// referenced nowhere else in apps/api). The existing
// GET /documents/:id/activity endpoint (documents.ts) already returns
// the document's audit_log rows (targetType='document', targetId=doc,
// newest first) — the same trail DocumentDetail's Activity tab renders.
// Reusing it means NO new server routes and NO new persistence.
//
// CAP GATE: there is no `documents:view_audit` capability in
// packages/shared/src/constants.ts. The closest existing cap is
// `audit:view` — the org audit-trail read capability (held by
// owner/operator roles, absent from restricted client PATs). The
// panel renders nothing without it. (The server endpoint itself is
// gated at documents:view; this client-side gate is the stricter
// audit-surface gate the ticket asks for.)

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiList } from '../lib/api.js';
import { useSession } from '../hooks/useSession.js';
import { CollapsiblePanel } from './CollapsiblePanel.js';

export interface DocumentActivityRow {
  id: string;
  actorId: string | null;
  action: string;
  targetType: string;
  targetId: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  message: string | null;
  timestamp: string;
}

export interface ActivityPanelProps {
  documentId: string;
  /** localStorage key suffix for the collapse state — keep per-tab
   *  unique, e.g. 'timeline-lane:activity:events.yaml'. */
  storageKey: string;
  /** Last-N cap. Default 10 per the ticket. */
  limit?: number;
}

/** "document.update" → "Update", "document.tag_add" → "Tag add" —
 *  same formatting stance as DocumentDetail's Activity tab. */
function formatAction(action: string): string {
  return action
    .replace(/^document\./, '')
    .replace(/_/g, ' ')
    .replace(/\./g, ' / ')
    .replace(/^./, (c) => c.toUpperCase());
}

/** Names of fields that differ between before/after. recordAudit for
 *  document.update wraps the row as {row: {...}} — unwrap when
 *  present. Bookkeeping fields that change on EVERY write are dropped
 *  so the summary says what the operator changed, not that the clock
 *  ticked. */
const NOISE_FIELDS = new Set(['updatedAt', 'contentHash', 'sizeBytes', 'etag']);

export function changedFieldSummary(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): string[] {
  const b = (before?.row as Record<string, unknown> | undefined) ?? before ?? {};
  const a = (after?.row as Record<string, unknown> | undefined) ?? after ?? {};
  const fields = new Set<string>([...Object.keys(b), ...Object.keys(a)]);
  return [...fields].filter(
    (f) => !NOISE_FIELDS.has(f) && JSON.stringify(b[f]) !== JSON.stringify(a[f]),
  );
}

export function ActivityPanel({ documentId, storageKey, limit = 10 }: ActivityPanelProps) {
  const { session } = useSession();
  const canView = session?.capabilities?.includes('audit:view') ?? false;

  const activityQ = useQuery({
    queryKey: ['document-activity', documentId],
    queryFn: () => apiList<DocumentActivityRow>(`/documents/${documentId}/activity`),
    // Cap-gated fetch: users without the audit cap never hit the
    // endpoint from this surface.
    enabled: canView,
  });

  const entries = useMemo(
    () => (activityQ.data?.data ?? []).slice(0, limit),
    [activityQ.data, limit],
  );

  if (!canView) return null;

  return (
    <CollapsiblePanel
      storageKey={storageKey}
      title="Recent changes"
      defaultCollapsed
      badge={activityQ.isLoading ? '…' : `${entries.length} shown`}
    >
      {activityQ.isLoading ? (
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>Loading activity…</p>
      ) : activityQ.isError ? (
        <p style={{ margin: 0, color: 'var(--danger-primary, #B3261E)', fontSize: 13 }}>
          Couldn&apos;t load activity for this document.
        </p>
      ) : entries.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>
          No recorded changes for this document yet.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {entries.map((e) => {
            const changed = changedFieldSummary(e.before, e.after);
            return (
              <div
                key={e.id}
                data-testid="activity-entry"
                style={{ borderLeft: '2px solid var(--accent-primary)', paddingLeft: 10, fontSize: 12 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                  <span>
                    <code style={{ fontFamily: 'var(--font-mono)' }}>{formatAction(e.action)}</code>
                    {' '}by {e.actorId ?? 'system'}
                  </span>
                  <span>{new Date(e.timestamp).toLocaleString()}</span>
                </div>
                <div style={{ marginTop: 2, color: 'var(--text-primary)' }}>
                  {changed.length > 0
                    ? <>Changed: <code style={{ fontFamily: 'var(--font-mono)' }}>{changed.join(', ')}</code></>
                    : e.message ?? 'No field-level diff recorded'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </CollapsiblePanel>
  );
}
